// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import {connect} from 'react-redux';
import type {ActionCreatorsMapObject, Dispatch} from 'redux';
import {bindActionCreators} from 'redux';

import {addMessageIntoHistory} from 'mattermost-redux/actions/posts';
import {Preferences, Permissions} from 'mattermost-redux/constants';
import {getConfig, getLicense} from 'mattermost-redux/selectors/entities/general';
import {getChannel} from 'mattermost-redux/selectors/entities/channels';
import {haveIChannelPermission} from 'mattermost-redux/selectors/entities/roles';
import {getCurrentTeamId, getTeam} from 'mattermost-redux/selectors/entities/teams';
import {getCurrentUserId, isCurrentUserSystemAdmin} from 'mattermost-redux/selectors/entities/users';
import {getBool, isCustomGroupsEnabled} from 'mattermost-redux/selectors/entities/preferences';

import {unsetEditingPost} from 'actions/post_actions';
import {openModal} from 'actions/views/modals';
import {editPost} from 'actions/views/posts';
import {getEditingPost} from 'selectors/posts';
import type {GlobalState} from 'types/store';
import Constants from 'utils/constants';

import {isDefaultChannel} from 'mattermost-redux/utils/channel_utils';

import type {Actions} from './edit_post';
import EditPost from './edit_post';

function mapStateToProps(state: GlobalState) {
    const config = getConfig(state);
    const editingPost = getEditingPost(state);
    const currentUserId = getCurrentUserId(state);
    const channelId = editingPost.post.channel_id;
    const teamId = getCurrentTeamId(state);
    const team = getTeam(state, teamId);
    const license = getLicense(state);

    const isAuthor = editingPost?.post?.user_id === currentUserId;
    const deletePermission = isAuthor ? Permissions.DELETE_POST : Permissions.DELETE_OTHERS_POSTS;
    const editPermission = isAuthor ? Permissions.EDIT_POST : Permissions.EDIT_OTHERS_POSTS;

    const channel = getChannel(state, channelId);
    const useChannelMentions = haveIChannelPermission(state, teamId, channelId, Permissions.USE_CHANNEL_MENTIONS);

    const isLDAPEnabled = license?.IsLicensed === 'true' && license?.LDAPGroups === 'true';
    const useGroupMentions = haveIChannelPermission(state, teamId, channelId, Permissions.USE_GROUP_MENTIONS);
    const useCustomGroupMentions = useGroupMentions && isCustomGroupsEnabled(state);
    const useLDAPGroupMentions = useGroupMentions && isLDAPEnabled;

    return {
        canEditPost: haveIChannelPermission(state, teamId, channelId, editPermission),
        canDeletePost: haveIChannelPermission(state, teamId, channelId, deletePermission),
        codeBlockOnCtrlEnter: getBool(state, Preferences.CATEGORY_ADVANCED_SETTINGS, 'code_block_ctrl_enter', true),
        ctrlSend: getBool(state, Preferences.CATEGORY_ADVANCED_SETTINGS, 'send_on_ctrl_enter'),
        config,
        editingPost,
        channelId,
        maxPostSize: parseInt(config.MaxPostSize || '0', 10) || Constants.DEFAULT_CHARACTER_LIMIT,
        readOnlyChannel: !isCurrentUserSystemAdmin(state) && channel && isDefaultChannel(channel, team),
        useChannelMentions,
        useGroupMentions: useCustomGroupMentions || useLDAPGroupMentions,
    };
}

function mapDispatchToProps(dispatch: Dispatch) {
    return {
        actions: bindActionCreators<ActionCreatorsMapObject<any>, Actions>({
            addMessageIntoHistory,
            editPost,
            unsetEditingPost,
            openModal,
        }, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(EditPost);
